/**
 * The player's state, and everything the HUD reads.
 *
 * Ported from `L2-MECANICAS/src/game/GameState.ts`. One mutable store with a
 * subscribe/notify pair, which is the same shape the settings module already
 * uses — the HUD listens, the game writes, nothing polls.
 *
 * What changed from the original:
 *  - `moveSpeed` is metres per second, not per frame.
 *  - level-up loops, so a kill worth three levels grants three.
 *  - buffs are a real list with durations, and `recalculateStats` folds them in
 *    alongside gear.
 *  - equipping returns the displaced item to the bag instead of orphaning it.
 */

import { ITEMS, SKILLS, QUESTS, CLASS_PROFILES, DEFAULT_HOTBAR } from "./worldData.js";

const SAVE_KEY = "trentino_online_state";

let uid = 0;
const nextId = (p) => `${p}_${Date.now().toString(36)}_${(uid++).toString(36)}`;

function clockLabel() {
    return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

class GameStateStore {
    constructor() {
        this.playerClass = "Human Knight";
        this.isSitting = false;
        this.isDead = false;

        this.stats = {
            level: 1,
            exp: 0,
            maxExp: 100,
            sp: 150,
            hp: 320,
            maxHp: 320,
            mp: 100,
            maxMp: 100,
            cp: 200,
            maxCp: 200,
            str: 40,
            dex: 30,
            con: 43,
            int: 21,
            wit: 20,
            men: 25,
            patk: 28,
            matk: 18,
            pdef: 32,
            mdef: 22,
            accuracy: 95,
            evasion: 15,
            critRate: 12,
            atkSpd: 320,
            castSpd: 280,
            /** Metres per second. The original's 0.18 was per frame at 60 Hz. */
            moveSpeed: 5.4,
            adena: 2500,
            soulshots: 500,
            soulshotActive: true,
        };

        /** Stats before gear and buffs, so recalculation is idempotent. */
        this.baseStats = { ...this.stats };

        this.equipment = {
            weapon: { id: nextId("eq"), item: ITEMS.short_sword, quantity: 1, isEquipped: true },
            shield: null,
            helmet: null,
            chest: { id: nextId("eq"), item: ITEMS.wooden_armor, quantity: 1, isEquipped: true },
            legs: null,
            gloves: null,
            boots: null,
            necklace: null,
            ring: null,
            earring: null,
        };

        this.inventory = [
            { id: nextId("inv"), item: ITEMS.hp_potion, quantity: 15 },
            { id: nextId("inv"), item: ITEMS.mp_potion, quantity: 10 },
            { id: nextId("inv"), item: ITEMS.soulshot_ng, quantity: 500 },
            { id: nextId("inv"), item: ITEMS.long_sword, quantity: 1 },
        ];

        this.hotbar = DEFAULT_HOTBAR.map((s, i) => ({
            id: i,
            keyLabel: `F${i + 1}`,
            type: s.type,
            targetId: s.targetId,
        }));

        /** Learned skill levels, keyed by skill id. Separate from the templates. */
        this.skillLevels = {};
        for (const id of Object.keys(SKILLS)) this.skillLevels[id] = 1;

        /** @type {{id:string,name:string,icon:string,until:number,mods:object}[]} */
        this.buffs = [];

        /** Accepted quests only. `QUESTS` is the catalogue. */
        this.quests = {};

        /** @type {{id:string,type:'mob'|'npc'}|null} */
        this.target = null;

        this.chatMessages = [];
        /** @type {{id:string,text:string,x:number,y:number,z:number,color:string,big:boolean,born:number}[]} */
        this.floatingTexts = [];

        /** @type {(() => void)[]} */
        this.listeners = [];

        this.addChat("System", "system", "Welcome to the snowfield.", true);
        this.addChat("System", "system", "Left-click to move, right-drag to look, ` for settings.", true);
        this.addChat("System", "system", "F1..F12 use the hotbar. I / C / K / U open your panels.", true);

        this.loadSavedState();
        this.recalculateStats();
    }

    // ------------------------------------------------------------ pub/sub
    subscribe(fn) {
        this.listeners.push(fn);
        return () => {
            this.listeners = this.listeners.filter((l) => l !== fn);
        };
    }

    notify() {
        for (const l of this.listeners) l();
    }

    // ------------------------------------------------------------- chat
    addChat(sender, channel, text, quiet = false) {
        this.chatMessages.push({
            id: nextId("msg"),
            sender,
            channel,
            text,
            timestamp: clockLabel(),
        });
        if (this.chatMessages.length > 80) this.chatMessages.shift();
        if (!quiet) this.notify();
    }

    system(text) {
        this.addChat("System", "system", text);
    }

    // ---------------------------------------------------- floating text
    addFloatingText(text, x, y, z, color = "#ef4444", big = false) {
        this.floatingTexts.push({
            id: nextId("float"),
            text,
            x, y, z,
            color,
            big,
            born: performance.now(),
        });
    }

    /** Drop expired entries. The original never did, and the array grew forever. */
    pruneFloatingTexts(lifetime = 1200) {
        const now = performance.now();
        const list = this.floatingTexts;
        let w = 0;
        for (let i = 0; i < list.length; i++) {
            if (now - list[i].born < lifetime) list[w++] = list[i];
        }
        list.length = w;
    }

    // ------------------------------------------------------------ stats
    /**
     * Derive combat stats from base attributes, gear and buffs.
     *
     * The four formulas are the original's, unchanged. Buffs apply after, as
     * flat adds and then multipliers, so "+300% P.Def" is a real multiplier
     * rather than another opaque constant.
     */
    recalculateStats() {
        const s = this.stats;
        let patk = 0, pdef = 0, matk = 0, mdef = 0;
        let hpBonus = 0, mpBonus = 0, critBonus = 0;

        for (const eq of Object.values(this.equipment)) {
            const b = eq && eq.item.statsBonus;
            if (!b) continue;
            patk += b.patk ?? 0;
            pdef += b.pdef ?? 0;
            matk += b.matk ?? 0;
            mdef += b.mdef ?? 0;
            hpBonus += b.hp ?? 0;
            mpBonus += b.mp ?? 0;
            critBonus += b.critRate ?? 0;
        }

        s.patk = s.str * 0.6 + patk + 15;
        s.pdef = s.con * 0.5 + pdef + 10;
        s.matk = s.int * 0.8 + matk + 10;
        s.mdef = s.men * 0.6 + mdef + 8;

        // Gear HP/MP/crit actually count now — the original collected them and
        // then threw them away.
        s.maxHp = this.baseStats.maxHp + hpBonus;
        s.maxMp = this.baseStats.maxMp + mpBonus;
        s.critRate = this.baseStats.critRate + critBonus;
        s.moveSpeed = this.baseStats.moveSpeed;
        s.atkSpd = this.baseStats.atkSpd;

        for (const b of this.buffs) {
            const m = b.mods;
            if (m.patk) s.patk += m.patk;
            if (m.pdef) s.pdef += m.pdef;
            if (m.matk) s.matk += m.matk;
            if (m.mdef) s.mdef += m.mdef;
            if (m.critRate) s.critRate += m.critRate;
            if (m.atkSpd) s.atkSpd += m.atkSpd;
            if (m.moveSpeed) s.moveSpeed += m.moveSpeed;
        }
        for (const b of this.buffs) {
            const m = b.mods;
            if (m.pdefMul) s.pdef *= m.pdefMul;
            if (m.mdefMul) s.mdef *= m.mdefMul;
            if (m.atkSpdMul) s.atkSpd *= m.atkSpdMul;
            if (m.moveSpeedMul) s.moveSpeed *= m.moveSpeedMul;
        }

        s.moveSpeed = Math.max(0.8, s.moveSpeed);
        s.hp = Math.min(s.hp, s.maxHp);
        s.mp = Math.min(s.mp, s.maxMp);
        s.cp = Math.min(s.cp, s.maxCp);
    }

    /** True while a buff with `rooted` is up — Ultimate Defense pins you. */
    get isRooted() {
        return this.buffs.some((b) => b.mods.rooted);
    }

    addBuff(def) {
        const now = performance.now();
        const existing = this.buffs.find((b) => b.id === def.id);
        if (existing) {
            existing.until = now + def.duration * 1000;
        } else {
            this.buffs.push({
                id: def.id,
                name: def.name,
                icon: def.icon,
                until: now + def.duration * 1000,
                mods: def.mods,
            });
        }
        this.recalculateStats();
        this.notify();
    }

    /** @returns {boolean} true if anything expired */
    expireBuffs() {
        const now = performance.now();
        const before = this.buffs.length;
        this.buffs = this.buffs.filter((b) => b.until > now);
        if (this.buffs.length !== before) {
            this.recalculateStats();
            return true;
        }
        return false;
    }

    // ------------------------------------------------------- progression
    addExp(amount, spAmount) {
        const s = this.stats;
        s.exp += amount;
        s.sp += spAmount;
        this.addChat("System", "system", `Earned ${amount} EXP and ${spAmount} SP.`, true);

        let levelled = false;
        // A loop, not an `if`: a raid boss is worth more than one level and the
        // surplus should not sit in the bank until the next kill.
        while (s.exp >= s.maxExp) {
            s.level += 1;
            s.exp -= s.maxExp;
            s.maxExp = Math.floor(s.maxExp * 1.5);

            this.baseStats.maxHp += 40;
            this.baseStats.maxMp += 20;
            s.maxCp += 30;
            levelled = true;
        }

        if (levelled) {
            this.recalculateStats();
            s.hp = s.maxHp;
            s.mp = s.maxMp;
            s.cp = s.maxCp;
            this.addChat("System", "system", `LEVEL UP! You are now Level ${s.level}!`, true);
        }
        this.saveState();
        this.notify();
        return levelled;
    }

    /** Death penalty, L2 style: a slice of the current level's experience. */
    applyDeathPenalty() {
        const s = this.stats;
        const lost = Math.floor(s.exp * 0.04);
        if (lost > 0) {
            s.exp -= lost;
            this.addChat("System", "system", `You lost ${lost} EXP.`, true);
        }
        return lost;
    }

    toggleSoulshot() {
        this.stats.soulshotActive = !this.stats.soulshotActive;
        this.system(`Auto-Soulshot ${this.stats.soulshotActive ? "ACTIVATED" : "DEACTIVATED"}.`);
    }

    setClass(className) {
        const profile = CLASS_PROFILES[className];
        if (!profile) return;
        this.playerClass = className;

        Object.assign(this.stats, profile.stats);
        this.stats.maxHp = this.stats.hp;
        this.stats.maxMp = this.stats.mp;
        this.baseStats = { ...this.stats };

        const weapon = ITEMS[profile.startingWeapon];
        if (weapon) {
            this.equipment.weapon = {
                id: nextId("eq"), item: weapon, quantity: 1, isEquipped: true,
            };
        }
        this.recalculateStats();
        this.system(`Class changed to ${className}!`);
        this.saveState();
        this.notify();
    }

    // -------------------------------------------------------- inventory
    /** @returns {object|null} the stack, or null */
    findStack(itemId) {
        return this.inventory.find((i) => i.item.id === itemId) ?? null;
    }

    addItem(itemId, quantity) {
        const item = ITEMS[itemId];
        if (!item) return;
        const stack = this.findStack(itemId);
        if (stack) stack.quantity += quantity;
        else this.inventory.push({ id: nextId("inv"), item, quantity });
    }

    /** @returns {boolean} whether the whole quantity was available */
    consumeItem(itemId, quantity = 1) {
        const stack = this.findStack(itemId);
        if (!stack || stack.quantity < quantity) return false;
        stack.quantity -= quantity;
        if (stack.quantity <= 0) {
            this.inventory = this.inventory.filter((i) => i !== stack);
        }
        return true;
    }

    equipItem(invItem) {
        const slot = invItem.item.slot;
        if (!slot) return;

        const current = this.equipment[slot];
        if (current) {
            current.isEquipped = false;
            // Back into the bag rather than into thin air.
            if (!this.inventory.includes(current)) this.inventory.push(current);
        }
        this.inventory = this.inventory.filter((i) => i !== invItem);
        invItem.isEquipped = true;
        this.equipment[slot] = invItem;

        this.recalculateStats();
        this.system(`Equipped ${invItem.item.name}.`);
        this.saveState();
        this.notify();
    }

    unequipItem(slot) {
        const current = this.equipment[slot];
        if (!current) return;
        current.isEquipped = false;
        this.equipment[slot] = null;
        if (!this.inventory.includes(current)) this.inventory.push(current);
        this.recalculateStats();
        this.system(`Unequipped ${current.item.name}.`);
        this.saveState();
        this.notify();
    }

    // ------------------------------------------------------------ quests
    acceptQuest(questId) {
        const def = QUESTS[questId];
        if (!def || this.quests[questId]) return false;
        this.quests[questId] = {
            questId,
            currentKills: 0,
            isCompleted: false,
            isTurnedIn: false,
        };
        this.system(`Quest accepted: ${def.title}.`);
        this.saveState();
        this.notify();
        return true;
    }

    turnInQuest(questId) {
        const def = QUESTS[questId];
        const progress = this.quests[questId];
        if (!def || !progress || !progress.isCompleted || progress.isTurnedIn) return false;

        progress.isTurnedIn = true;
        this.stats.adena += def.rewardAdena;
        if (def.rewardItemId) this.addItem(def.rewardItemId, 1);
        this.system(`Quest complete: ${def.title}. ${def.rewardAdena} adena received.`);
        this.addExp(def.rewardExp, 0);
        this.saveState();
        this.notify();
        return true;
    }

    /** @returns {boolean} true if a quest advanced */
    creditKill(mobDataId) {
        let changed = false;
        for (const [id, progress] of Object.entries(this.quests)) {
            const def = QUESTS[id];
            if (!def || def.targetMobId !== mobDataId) continue;
            if (progress.isCompleted) continue;
            progress.currentKills += 1;
            changed = true;
            if (progress.currentKills >= def.requiredKills) {
                progress.isCompleted = true;
                this.addChat("System", "system",
                    `${def.title} complete — return to the quest giver.`, true);
            }
        }
        return changed;
    }

    // ----------------------------------------------------------- skills
    skillLevel(id) {
        return this.skillLevels[id] ?? 1;
    }

    learnSkill(id) {
        const skill = SKILLS[id];
        if (!skill) return false;
        if (this.stats.level < skill.requiredLevel) {
            this.system(`${skill.name} requires level ${skill.requiredLevel}.`);
            return false;
        }
        if (this.stats.sp < skill.spCost) {
            this.system(`Not enough SP to learn ${skill.name}.`);
            return false;
        }
        this.stats.sp -= skill.spCost;
        this.skillLevels[id] = this.skillLevel(id) + 1;
        this.system(`Learned ${skill.name} Lv ${this.skillLevels[id]}.`);
        this.saveState();
        this.notify();
        return true;
    }

    // ------------------------------------------------------ persistence
    saveState() {
        try {
            localStorage.setItem(SAVE_KEY, JSON.stringify({
                v: 1,
                stats: this.stats,
                baseStats: this.baseStats,
                playerClass: this.playerClass,
                inventory: this.inventory.map(serialiseStack),
                equipment: Object.fromEntries(
                    Object.entries(this.equipment).map(([k, v]) => [k, v ? serialiseStack(v) : null])
                ),
                quests: this.quests,
                skillLevels: this.skillLevels,
                hotbar: this.hotbar,
            }));
        } catch {
            // A full or disabled localStorage is not worth interrupting play for.
        }
    }

    loadSavedState() {
        let parsed;
        try {
            const raw = localStorage.getItem(SAVE_KEY);
            if (!raw) return;
            parsed = JSON.parse(raw);
        } catch {
            return;
        }
        if (!parsed || parsed.v !== 1) return;

        if (parsed.stats) Object.assign(this.stats, parsed.stats);
        if (parsed.baseStats) Object.assign(this.baseStats, parsed.baseStats);
        if (parsed.playerClass) this.playerClass = parsed.playerClass;
        if (parsed.quests) this.quests = parsed.quests;
        if (parsed.skillLevels) Object.assign(this.skillLevels, parsed.skillLevels);
        if (Array.isArray(parsed.hotbar) && parsed.hotbar.length === 12) {
            this.hotbar = parsed.hotbar;
        }
        if (Array.isArray(parsed.inventory)) {
            this.inventory = parsed.inventory.map(reviveStack).filter(Boolean);
        }
        if (parsed.equipment) {
            for (const [slot, stack] of Object.entries(parsed.equipment)) {
                if (slot in this.equipment) this.equipment[slot] = stack ? reviveStack(stack) : null;
            }
        }
        this.system("Saved character restored.");
    }

    resetSave() {
        try {
            localStorage.removeItem(SAVE_KEY);
        } catch {
            // ignore
        }
    }
}

/** Items are stored by id — the catalogue is code, not save data. */
function serialiseStack(stack) {
    return {
        id: stack.id,
        itemId: stack.item.id,
        quantity: stack.quantity,
        isEquipped: !!stack.isEquipped,
    };
}

function reviveStack(raw) {
    const item = ITEMS[raw.itemId];
    if (!item) return null;
    return { id: raw.id, item, quantity: raw.quantity, isEquipped: !!raw.isEquipped };
}

export const gameState = new GameStateStore();
